import { chain } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as MoreIcon } from '@src/assets/escher/icons/expand-less.svg';
import { ReactComponent as LessIcon } from '@src/assets/escher/icons/expand-more.svg';

import HitFeature from './HitFeature';
import ThresholdGraph from './ThresholdGraph';

const OtherFactorsLabel = styled.div`
  display: inline-block;
  cursor: pointer;
  margin-top: 30px;
  svg {
    vertical-align: bottom;
  }
`;

const OtherFactorContainer = styled.div`
  filter: grayscale(100%);
  color: ${props => props.theme.enigmaGrey};
  opacity: 0.8;
  margin: 24px;
`;

const TopFiveHeader = styled.div`
  display: inline-block;
  margin-top: 15px;
`;

const FeatureContainer = styled.div`
  display: inline-block;
`;

const FEATURE_CUTOFF = 5;

const Row = styled.div`
  width: 100%;
  margin: 10px 0px;
`;

function RuleSummary({
  decision,
  isRuleBased,
  isModelBased,
  isThresholdBased,
  explanation: { override, model, threshold } = {},
}) {
  const hasAutoClear = override && override.type && override.decision === 0;
  const hasAutoEscalate = override && override.type && override.decision === 1;
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = () => setExpanded(!expanded);

  let list = model ? model.feature_explanation : [];

  list = chain(list)
    .sortBy(item => Math.abs(item.value))
    .reverse()
    .take(FEATURE_CUTOFF)
    .value();

  let aboveFold;
  let belowFold;

  const modelGraph = (
    <ThresholdGraph
      value={model && model.score}
      thresholdValue={model && model.threshold}
    />
  );


  const featureList = (
    <FeatureContainer>
      <TopFiveHeader>
        Top 5 feature contributors to Match likelihood
      </TopFiveHeader>
      <div>
        <div>
          {list.map(item => (
            <HitFeature {...item} />
          ))}
        </div>
      </div>
    </FeatureContainer>
  );

  const escalationContent = (
    <div>
      {override?.description?.length > 0 && (
        <Row>Rule description: {override.description}</Row>
      )}
      {!hasAutoEscalate && <Row>No Auto-escalate rules triggered</Row>}
      {!hasAutoClear && <Row>No Auto-clear rules triggered</Row>}
    </div>
  );

  if (isRuleBased) {
    aboveFold = escalationContent;
    belowFold = (
      <div>
        {modelGraph}
        {featureList}
      </div>
    );
  } else if (isModelBased) {
    aboveFold = featureList;
    belowFold = (
      <div>
        {escalationContent}
      </div>
    );
  } else if (isThresholdBased) {
    // aboveFold = ??? I don't have a reference for this in the wires
    belowFold = (
      <div>
        {modelGraph}
        {featureList}
        {escalationContent}
      </div>
    );
  }

  return (
    <div>
      {aboveFold}
      <OtherFactorsLabel onClick={toggleExpanded}>
        {expanded ? <MoreIcon /> : <LessIcon />}
        Other factors not impacting the decision
      </OtherFactorsLabel>
      {expanded && <OtherFactorContainer>{belowFold}</OtherFactorContainer>}
    </div>
  );
}

RuleSummary.propTypes = {
  explanation: PropTypes.shape({
    override: PropTypes.shape({
      decision: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }),
    model: PropTypes.shape({
      decision: PropTypes.number.isRequired,
      score: PropTypes.number.isRequired,
      type: PropTypes.string.isRequired,
    }),
    threshold: PropTypes.shape({
      type: PropTypes.string.isRequired,
      decision: PropTypes.number.isRequired,
      minimum_score: PropTypes.number.isRequired,
      score: PropTypes.number.isRequired,
    }),
  }).isRequired,
};

export default RuleSummary;
